// ScanItem — close-up, single-item capture flow.
//
// Sibling to RoomScan (wide-angle). Differences:
//   - Captures ONE close-up photo of a deliberately-framed item
//   - Edge Function call uses capture_mode='single_item' (no Google Vision)
//   - Review screen is a single editable form, not an N-row list
//   - Save writes ONE row to room_inventory directly
//   - "Change Room" lets the user reassign or unset room_id before save
//
// Layout intentionally mirrors RoomScan's edit view so the muscle-memory
// transfers. ~250 lines of duplication is acceptable tonight; a future
// session can extract a shared <ItemEditView/> once both consumers exist.

import { useState, useRef, useEffect } from 'react'
import { Camera, X, Loader, Image as ImageIcon, Check } from 'lucide-react'
import { supabase } from '../supabase'
import { convertHeicIfNeeded } from '../utils/convertHeicIfNeeded'
import { stripExif } from '../utils/stripExif'
import { compressImage } from '../imageUtils'
import { mergeFillNullsFromSingleItemAi } from './RoomScan'

// Multi-photo capture cap. Each shot fires its own single_item POST to
// enumerate-room-items and merges via fill-nulls into one in-memory working
// item. The Edge Function v9 creates a brand-new room_scan_sessions row per
// call, so N shots = N session rows. Only the FIRST session's id is recorded
// as source_capture_id on the saved room_inventory row.
// TODO(orphan-sessions): the N-1 trailing sessions become orphans. Either
// delete non-primary sessions on save, OR add a parent_session_id column to
// room_scan_sessions and group them. Deferred — not blocking tonight.
const MAX_CAPTURE_SHOTS = 6
const MAX_DETAIL_PHOTOS_TOTAL = 10

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
}
const f = 'Georgia, serif'
const fs = 'Calibri, sans-serif'

const CATEGORIES = [
  'Furniture', 'Electronics', 'Appliances',
  'Housewares', 'Linens', 'Décor',
  'Bags & Luggage', 'Books & Media',
  'Jewelry', 'Art', 'Clothing', 'Tools',
  'Musical Instruments', 'Sports Equipment', 'Collectibles',
  'Other',
]
const CATEGORIES_SET = new Set(CATEGORIES)

const SPIN_STYLE = `@keyframes scanitem-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`

const inp = {
  width: '100%', background: '#fff', border: `1px solid ${C.border}`,
  borderRadius: '8px', padding: '10px 12px', fontSize: '15px',
  fontFamily: fs, color: C.brown, boxSizing: 'border-box', outline: 'none',
  WebkitAppearance: 'none',
}
const lbl = {
  fontSize: '10px', color: C.muted, letterSpacing: '1.5px',
  textTransform: 'uppercase', fontFamily: fs, fontWeight: '600',
  display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px',
}

function confDotColor(conf) {
  if (conf === 'high') return C.success
  if (conf === 'medium') return C.amber
  return C.muted
}
function ConfDot({ conf }) {
  if (!conf) return null
  return (
    <span
      title={conf === 'high' ? 'AI is highly confident' : conf === 'medium' ? "AI's best guess — please verify" : 'AI is uncertain — please review'}
      style={{
        display: 'inline-block', width: '8px', height: '8px',
        borderRadius: '50%', background: confDotColor(conf),
        flexShrink: 0,
      }}
    />
  )
}

export default function ScanItem({ room, homeId, onClose, onSaved, sourcePreference = 'camera' }) {
  // capture | uploading | between_shots | uploading_more | review | saving | error
  const [state, setState] = useState('capture')
  const [errorMessage, setErrorMessage] = useState(null)
  const [firstResponse, setFirstResponse] = useState(null) // first Edge Function response — supplies session_id + primary photo
  const [item, setItem] = useState(null) // mutable working item, AI-merged across shots

  // Multi-photo capture session (Part A — between_shots flow).
  //   { localId, url, sessionId, aiStatus: 'pending'|'success'|'error' }
  // First entry is the primary photo (thumbnail). All entries go into
  // photo_urls on save. AI extraction per shot fills `item` via fill-nulls.
  const [shots, setShots] = useState([])
  const [moreShotError, setMoreShotError] = useState(null)
  const [continueAwaiting, setContinueAwaiting] = useState(false)
  const pendingExtractionsRef = useRef(new Map()) // localId -> Promise
  const shotIdCounterRef = useRef(0)

  // Detail photos uploaded while on the review screen (upload-only, no AI).
  // Distinct from `shots` — these append to photo_urls but don't contribute
  // to AI extraction. Kept for after-the-fact photo additions on the
  // review screen.
  const [photoUploading, setPhotoUploading] = useState(false)
  const [photoError, setPhotoError] = useState(null)

  // Change Room support.
  const [roomsList, setRoomsList] = useState([])
  const [chosenRoomId, setChosenRoomId] = useState(room?.id || null)

  // Discard confirmation when user X's out of between_shots or review.
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  const cameraInputRef = useRef(null)
  const libraryInputRef = useRef(null)
  const moreCameraRef = useRef(null)    // between_shots: add another
  const moreLibraryRef = useRef(null)   // between_shots: add from library
  const detailCameraRef = useRef(null)  // review-screen: extra detail photos (no AI)
  const detailLibraryRef = useRef(null)
  const autoLaunchedRef = useRef(false)

  const nextShotId = () => {
    shotIdCounterRef.current += 1
    return `shot-${shotIdCounterRef.current}`
  }

  // ----- Auto-launch capture once on initial mount --------------------------
  useEffect(() => {
    if (state !== 'capture') return
    if (autoLaunchedRef.current) return
    autoLaunchedRef.current = true
    const handle = requestAnimationFrame(() => {
      if (sourcePreference === 'library') libraryInputRef.current?.click()
      else cameraInputRef.current?.click()
    })
    return () => cancelAnimationFrame(handle)
  }, [state, sourcePreference])

  // ----- Load rooms list for the Change Room dropdown -----------------------
  useEffect(() => {
    let cancelled = false
    async function loadRooms() {
      if (!homeId) return
      const { data } = await supabase
        .from('rooms')
        .select('id, name')
        .eq('home_id', homeId)
        .order('name', { ascending: true })
      if (cancelled) return
      if (Array.isArray(data)) setRoomsList(data)
    }
    loadRooms()
    return () => { cancelled = true }
  }, [homeId])

  // ----- Helpers ------------------------------------------------------------

  function resolveCategory(suggestion) {
    if (typeof suggestion === 'string' && CATEGORIES_SET.has(suggestion)) return suggestion
    return 'Other'
  }

  async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const dataUrl = e.target.result
        const base64 = String(dataUrl).split(',')[1] || ''
        resolve(base64)
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  // ----- Capture: first shot ------------------------------------------------
  // First shot uses the same local-blob-then-background-AI pattern as
  // subsequent shots so between_shots opens immediately on shutter release.
  // The Edge Function call runs in the background; the working item is built
  // (and `firstResponse` recorded) when the first successful AI response
  // arrives — see runShotExtraction below.

  async function handleFileSelected(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    setState('uploading')
    setErrorMessage(null)

    let processed
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      processed = await compressImage(stripped, 1600, 0.80)
    } catch (err) {
      console.error('[ScanItem] preprocess error', err)
      setErrorMessage(err?.message || 'Could not process this photo. Please try another.')
      setState('error')
      return
    }

    // Local thumbnail placeholder + transition to between_shots immediately.
    // The Edge Function call (upload + Claude, ~10s) runs in the background.
    const localBlobUrl = URL.createObjectURL(processed)
    const localId = nextShotId()
    setShots([{ localId, url: localBlobUrl, sessionId: null, aiStatus: 'pending', isBlob: true }])
    setState('between_shots')

    const promise = runShotExtraction(processed, localId)
    pendingExtractionsRef.current.set(localId, promise)
  }

  // Shared background extraction: uploads + Claude POST; on success, swaps
  // the local blob for the signed URL on the shot, and either BUILDS the
  // working item (if this is the first successful response) or fill-nulls-
  // merges into the existing one. Uses functional setState updates so the
  // first response to return atomically wins the "build" role regardless of
  // shot order — robust to a first-shot AI failure followed by a successful
  // second shot.

  async function runShotExtraction(processedBlob, localId) {
    let base64
    try {
      base64 = await fileToBase64(processedBlob)
    } catch (err) {
      console.error('[ScanItem] shot base64 error', err)
      setShots(prev => prev.map(s => s.localId === localId ? { ...s, aiStatus: 'error' } : s))
      pendingExtractionsRef.current.delete(localId)
      return
    }
    try {
      const { data, error } = await supabase.functions.invoke('enumerate-room-items', {
        body: {
          capture_mode: 'single_item',
          photo_base64: base64,
          room_id: room?.id,
          home_id: homeId,
          media_type: 'image/jpeg',
        },
      })
      if (error || !data?.session_id || !Array.isArray(data.items) || data.items.length === 0) {
        console.error('[ScanItem] shot edge function error', error || 'empty response')
        setShots(prev => prev.map(s => s.localId === localId ? { ...s, aiStatus: 'error' } : s))
        return
      }
      const aiItem = data.items[0]
      const det = aiItem.ai_details || {}
      const realUrl = data.photo_url || aiItem.thumbnail_url || null

      setShots(prev => prev.map(s => {
        if (s.localId !== localId) return s
        if (s.isBlob && s.url) {
          try { URL.revokeObjectURL(s.url) } catch (_) { /* noop */ }
        }
        return { ...s, url: realUrl, sessionId: data.session_id, aiStatus: 'success', isBlob: false }
      }))

      // First successful response wins: builds the working item. Subsequent
      // responses fill-nulls-merge into it. Functional updaters keep this
      // race-safe.
      setFirstResponse(prev => prev === null ? data : prev)
      setItem(prev => {
        if (prev === null) {
          return {
            name: aiItem.refined_label || aiItem.label || 'Item',
            refined_label: aiItem.refined_label || aiItem.label || 'Item',
            category: resolveCategory(det.category_suggestion),
            brand: det.brand?.value || '',
            brand_confidence: det.brand?.confidence || null,
            model_number: det.model?.value || '',
            model_confidence: det.model?.confidence || null,
            serial_number: det.serial || '',
            manufactured: det.manufactured || '',
            color: det.color || '',
            material: det.material || '',
            size: det.size_estimate?.value || '',
            size_confidence: det.size_estimate?.confidence || null,
            notes: '',
            photo_urls: [],
            thumbnail_url: aiItem.thumbnail_url || data.photo_url || null,
            confidence_band: aiItem.confidence_band || 'high',
          }
        }
        const { next } = mergeFillNullsFromSingleItemAi(prev, det)
        return next
      })
    } catch (err) {
      console.error('[ScanItem] shot extraction exception', err)
      setShots(prev => prev.map(s => s.localId === localId ? { ...s, aiStatus: 'error' } : s))
    } finally {
      pendingExtractionsRef.current.delete(localId)
    }
  }

  // ----- between_shots: add another shot in the same capture session --------
  // Same local-blob-then-background-AI pattern as the first shot. The Edge
  // Function call runs in the background via runShotExtraction; when it
  // resolves, the shot's url/sessionId update and the AI result fill-nulls-
  // merges into `item` (or builds it, if this happens to be the first
  // successful response back).

  async function handleAdditionalShot(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (shots.length >= MAX_CAPTURE_SHOTS) return

    setMoreShotError(null)
    setState('uploading_more')

    let processed
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      processed = await compressImage(stripped, 1600, 0.80)
    } catch (err) {
      console.error('[ScanItem] additional shot preprocess error', err)
      setMoreShotError(err?.message || 'Could not process this photo. Please try another.')
      setState('between_shots')
      return
    }

    const localBlobUrl = URL.createObjectURL(processed)
    const localId = nextShotId()
    setShots(prev => [...prev, { localId, url: localBlobUrl, sessionId: null, aiStatus: 'pending', isBlob: true }])
    setState('between_shots')

    const promise = runShotExtraction(processed, localId)
    pendingExtractionsRef.current.set(localId, promise)
  }

  async function handleContinueFromBetweenShots() {
    // Await all pending background extractions so the review screen isn't a
    // moving target. Show a brief "Finishing…" state while we wait.
    const pending = Array.from(pendingExtractionsRef.current.values())
    if (pending.length > 0) {
      setContinueAwaiting(true)
      try {
        await Promise.all(pending)
      } catch (_) { /* per-promise errors already surface in shot.aiStatus */ }
      setContinueAwaiting(false)
    }
    // If every shot's AI call failed (no working item built), there's
    // nothing to review and nothing to save. Surface as an error rather
    // than rendering a blank review.
    if (!item) {
      setErrorMessage("We couldn't read any of your photos. Try framing the data plate or branding more directly.")
      setState('error')
      return
    }
    setState('review')
  }

  function removeShot(localId) {
    setShots(prev => {
      const target = prev.find(s => s.localId === localId)
      if (target?.isBlob && target.url) {
        try { URL.revokeObjectURL(target.url) } catch (_) { /* noop */ }
      }
      return prev.filter(s => s.localId !== localId)
    })
    pendingExtractionsRef.current.delete(localId)
  }

  // ----- Review: detail photo upload ----------------------------------------

  async function handleDetailPhotoSelect(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !item) return
    // Cap counts shots + review-screen extras combined.
    const reviewExtras = item.photo_urls?.length || 0
    const totalSoFar = shots.length + reviewExtras
    if (totalSoFar >= MAX_DETAIL_PHOTOS_TOTAL) {
      setPhotoError(`You've reached the limit of ${MAX_DETAIL_PHOTOS_TOTAL} photos per item.`)
      return
    }
    setPhotoUploading(true)
    setPhotoError(null)
    try {
      const jpeg = await convertHeicIfNeeded(file)
      const stripped = await stripExif(jpeg)
      const processed = await compressImage(stripped, 1600, 0.80)
      const userResp = await supabase.auth.getUser()
      const userId = userResp?.data?.user?.id
      const sessionId = firstResponse?.session_id
      if (!userId || !sessionId) throw new Error('Session expired. Please retry the scan.')
      const path = `${userId}/${sessionId}/details/${Date.now()}.jpg`
      const { error: upErr } = await supabase.storage
        .from('room-scans')
        .upload(path, processed, { contentType: 'image/jpeg', upsert: false })
      if (upErr) throw upErr
      const { data: signed, error: signErr } = await supabase.storage
        .from('room-scans')
        .createSignedUrl(path, 60 * 60 * 24 * 365)
      if (signErr || !signed?.signedUrl) throw new Error('Could not generate a link for the photo.')
      setItem(prev => prev
        ? { ...prev, photo_urls: [...(prev.photo_urls || []), signed.signedUrl] }
        : prev,
      )
    } catch (err) {
      console.error('[ScanItem] detail photo upload error', err)
      setPhotoError(err.message || 'Could not add that photo. Please try another.')
    } finally {
      setPhotoUploading(false)
    }
  }

  function removeDetailPhoto(idx) {
    setItem(prev => prev
      ? { ...prev, photo_urls: (prev.photo_urls || []).filter((_, i) => i !== idx) }
      : prev,
    )
  }

  // ----- Save ---------------------------------------------------------------

  async function handleSave() {
    if (!item || !firstResponse?.session_id) return
    setState('saving')
    try {
      const userResp = await supabase.auth.getUser()
      const userId = userResp?.data?.user?.id
      if (!userId) {
        setErrorMessage("Couldn't confirm your account. Please try again.")
        setState('error')
        return
      }

      // Build photo_urls: all successfully-uploaded shots from the capture
      // session (in order) + any review-screen extras. Skip shots whose
      // Edge Function call failed (no real URL exists).
      const shotUrls = shots
        .filter(s => !s.isBlob && typeof s.url === 'string' && s.url.length > 0)
        .map(s => s.url)
      const reviewExtras = Array.isArray(item.photo_urls) ? item.photo_urls : []
      const allPhotoUrls = [...shotUrls, ...reviewExtras]
      const primaryUrl = shotUrls[0] || item.thumbnail_url || null

      const payload = {
        room_id: chosenRoomId || null,
        home_id: homeId,
        user_id: userId,
        name: item.name?.trim() || item.refined_label || 'Item',
        category: item.category || null,
        brand: item.brand?.trim() || null,
        model_number: item.model_number?.trim() || null,
        color: item.color?.trim() || null,
        material: item.material?.trim() || null,
        ai_confidence: item.confidence_band || 'high',
        ai_extracted: true,
        position_hint: null,
        bounding_box: null,
        source_capture_id: firstResponse.session_id,
        detail_photo_url: primaryUrl,
        serial_number: item.serial_number?.trim() || null,
        manufactured: item.manufactured?.trim() || null,
        notes: item.notes?.trim() || null,
        photo_urls: allPhotoUrls.length > 0 ? allPhotoUrls : null,
      }

      const { data: inserted, error: insertErr } = await supabase
        .from('room_inventory')
        .insert(payload)
        .select()
        .single()

      if (insertErr) {
        console.error('[ScanItem] insert error', insertErr)
        setErrorMessage("Couldn't save this item. Please try again.")
        setState('error')
        return
      }

      // Mark the primary session as saved. The trailing N-1 multi-shot
      // sessions are left as 'reviewing' for now (see orphan-sessions TODO
      // at top of file).
      await supabase
        .from('room_scan_sessions')
        .update({ status: 'saved', items_saved: 1 })
        .eq('id', firstResponse.session_id)

      onSaved(inserted)
    } catch (err) {
      console.error('[ScanItem] save exception', err)
      setErrorMessage("Couldn't save this item. Please try again.")
      setState('error')
    }
  }

  // ----- Close handler ------------------------------------------------------

  function handleHeaderClose() {
    if (state === 'uploading' || state === 'uploading_more' || state === 'saving') return
    if (state === 'between_shots' || state === 'review') {
      setConfirmDiscard(true)
    } else {
      onClose()
    }
  }

  function setField(k, v) {
    setItem(prev => prev ? { ...prev, [k]: v } : prev)
  }

  // ----- Layout primitives --------------------------------------------------

  const overlay = {
    position: 'fixed', inset: 0, background: C.bg, zIndex: 1500,
    display: 'flex', flexDirection: 'column',
  }
  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
    background: C.card, flexShrink: 0,
  }
  const closeBtn = {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: C.muted, padding: '6px', borderRadius: '8px', lineHeight: 0,
  }
  const scrollArea = { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }
  const centerPane = {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px', textAlign: 'center',
  }

  // ----- Renderers ----------------------------------------------------------

  function renderHeader(title, hideClose = false) {
    return (
      <div style={headerStyle}>
        <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown }}>{title}</div>
        {!hideClose && (
          <button onClick={handleHeaderClose} aria-label="Close" style={closeBtn}>
            <X size={22} color={C.muted} />
          </button>
        )}
      </div>
    )
  }

  function renderCapture() {
    return (
      <>
        {renderHeader('Item Photo')}
        <div style={centerPane}>
          <Camera size={96} strokeWidth={1.2} color={C.amber} />
          <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
            Photograph a single item
          </div>
          <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, marginTop: '10px', maxWidth: '320px', lineHeight: 1.55 }}>
            Take a close-up of the item. Frame the branding, data plate, or serial label so we can read it.
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <input
            ref={libraryInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelected}
          />
          <button
            onClick={() => cameraInputRef.current?.click()}
            style={{
              marginTop: '28px',
              background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '14px 28px', fontFamily: f, fontSize: '15px',
              fontWeight: '700', cursor: 'pointer', letterSpacing: '0.3px',
              boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
              display: 'inline-flex', alignItems: 'center', gap: '10px',
            }}
          >
            <Camera size={18} strokeWidth={2} color="#fff" />
            Take Photo
          </button>
          <button
            onClick={() => libraryInputRef.current?.click()}
            style={{
              marginTop: '10px',
              background: 'transparent', color: C.brown,
              border: `1px solid ${C.border}`, borderRadius: '10px',
              padding: '11px 22px', fontFamily: fs, fontSize: '14px',
              fontWeight: '600', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: '8px',
            }}
          >
            <ImageIcon size={16} strokeWidth={2} color={C.brown} />
            Add from Camera Roll
          </button>
          <button
            onClick={onClose}
            style={{
              marginTop: '14px', background: 'transparent', border: 'none',
              color: C.muted, fontFamily: fs, fontSize: '14px',
              textDecoration: 'underline', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </>
    )
  }

  function renderUploading() {
    return (
      <>
        {renderHeader('Item Photo', true)}
        <div style={centerPane}>
          <Loader
            size={64}
            color={C.amber}
            strokeWidth={1.5}
            style={{ animation: 'scanitem-spin 1s linear infinite' }}
          />
          <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
            Looking at this item…
          </div>
          <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, marginTop: '10px', maxWidth: '320px', lineHeight: 1.55 }}>
            Reading the brand, model, and serial. This usually takes about 10 seconds.
          </div>
        </div>
      </>
    )
  }

  function renderSaving() {
    return (
      <>
        {renderHeader('Item Photo', true)}
        <div style={centerPane}>
          <Loader
            size={64}
            color={C.amber}
            strokeWidth={1.5}
            style={{ animation: 'scanitem-spin 1s linear infinite' }}
          />
          <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
            Saving to inventory…
          </div>
        </div>
      </>
    )
  }

  function renderError() {
    return (
      <>
        {renderHeader('Item Photo')}
        <div style={centerPane}>
          <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginBottom: '12px' }}>
            Let's try that again
          </div>
          <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, maxWidth: '340px', lineHeight: 1.6, marginBottom: '24px' }}>
            {errorMessage || 'Something went wrong.'}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => { setErrorMessage(null); setState('capture'); autoLaunchedRef.current = false }}
              style={{
                background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                color: '#fff', border: 'none', borderRadius: '10px',
                padding: '12px 22px', fontFamily: f, fontSize: '14px',
                fontWeight: '700', cursor: 'pointer',
              }}
            >
              Try Again
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent', color: C.brown,
                border: `1px solid ${C.border}`, borderRadius: '10px',
                padding: '12px 22px', fontFamily: fs, fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      </>
    )
  }

  function renderBetweenShots() {
    // Note: item may be null until the first successful AI response arrives.
    // We still render the strip + Add/Continue so the user can capture more
    // shots in parallel. Continue awaits all pending extractions before
    // advancing to review.
    const count = shots.length
    const atCap = count >= MAX_CAPTURE_SHOTS

    // While the Continue handler is awaiting pending extractions, show a
    // dedicated "finishing" state instead of the take-another UI. This
    // prevents the user from triggering more shots while we're winding down.
    if (continueAwaiting) {
      return (
        <>
          {renderHeader('Item Photo', true)}
          <div style={centerPane}>
            <Loader
              size={64}
              color={C.amber}
              strokeWidth={1.5}
              style={{ animation: 'scanitem-spin 1s linear infinite' }}
            />
            <div style={{ fontFamily: f, fontSize: '22px', fontWeight: '700', color: C.brown, marginTop: '20px' }}>
              Finishing up…
            </div>
            <div style={{ fontFamily: fs, fontSize: '14px', color: C.muted, marginTop: '10px', maxWidth: '320px', lineHeight: 1.55 }}>
              Reading the last of the photos before showing your review.
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        {renderHeader('Item Photo')}
        <div style={scrollArea}>
          {/* Counter pill */}
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '14px' }}>
            <div style={{
              background: C.bg, border: `1px solid ${C.border}`,
              borderRadius: '999px', padding: '5px 14px',
              fontFamily: fs, fontSize: '12px', color: C.brown, fontWeight: '600',
              letterSpacing: '0.4px',
            }}>
              {count} of {MAX_CAPTURE_SHOTS} photos
            </div>
          </div>

          {/* Hint */}
          <div style={{ textAlign: 'center', padding: '20px 24px 8px' }}>
            <div style={{ fontFamily: f, fontSize: '20px', fontWeight: '700', color: C.brown, marginBottom: '6px' }}>
              Add another angle?
            </div>
            <div style={{ fontFamily: fs, fontSize: '13px', color: C.muted, maxWidth: '320px', margin: '0 auto', lineHeight: 1.55 }}>
              {atCap
                ? 'Max photos reached. Tap Continue to review.'
                : 'Try the data plate, serial label, or paperwork — each shot fills in more details.'}
            </div>
          </div>

          {/* Add another / library inputs (hidden) */}
          <input
            ref={moreCameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleAdditionalShot}
          />
          <input
            ref={moreLibraryRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleAdditionalShot}
          />

          {/* Action buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px 18px 0', alignItems: 'center' }}>
            <button
              onClick={() => moreCameraRef.current?.click()}
              disabled={atCap}
              style={{
                width: '100%', maxWidth: '320px',
                background: atCap ? C.bg : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                color: atCap ? C.muted : '#fff',
                border: 'none', borderRadius: '10px',
                padding: '16px 22px', fontFamily: f, fontSize: '15px',
                fontWeight: '700',
                cursor: atCap ? 'not-allowed' : 'pointer',
                opacity: atCap ? 0.7 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                boxShadow: atCap ? 'none' : '0 2px 6px rgba(184,114,31,0.25)',
              }}
            >
              <Camera size={20} strokeWidth={2} color={atCap ? C.muted : '#fff'} />
              Add another
            </button>
            <button
              onClick={() => moreLibraryRef.current?.click()}
              disabled={atCap}
              style={{
                width: '100%', maxWidth: '320px',
                background: '#fff', color: C.brown,
                border: `1px solid ${C.border}`, borderRadius: '10px',
                padding: '11px 22px', fontFamily: fs, fontSize: '14px',
                fontWeight: '600',
                cursor: atCap ? 'not-allowed' : 'pointer',
                opacity: atCap ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
            >
              <ImageIcon size={16} strokeWidth={2} color={C.brown} />
              Add from Camera Roll
            </button>
            {moreShotError && (
              <div style={{ fontFamily: fs, fontSize: '12px', color: C.danger, textAlign: 'center', maxWidth: '320px' }}>
                {moreShotError}
              </div>
            )}
          </div>

          <div style={{ height: '20px' }} />
        </div>

        {/* Bottom: thumbnail strip + Continue */}
        <div style={{
          flexShrink: 0,
          borderTop: `1px solid ${C.border}`,
          background: C.card,
          padding: '12px 14px calc(18px + env(safe-area-inset-bottom))',
        }}>
          {/* Thumbnails strip — newest on the right */}
          <div style={{
            display: 'flex', gap: '8px', overflowX: 'auto',
            paddingBottom: '10px', WebkitOverflowScrolling: 'touch',
          }}>
            {shots.map(s => (
              <div key={s.localId} style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={s.url || ''}
                  alt=""
                  style={{
                    width: '64px', height: '64px', objectFit: 'cover',
                    borderRadius: '8px', background: C.bg,
                    opacity: s.aiStatus === 'error' ? 0.5 : 1,
                  }}
                />
                {s.aiStatus === 'pending' && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.25)', borderRadius: '8px',
                  }}>
                    <Loader
                      size={20}
                      color="#fff"
                      strokeWidth={2}
                      style={{ animation: 'scanitem-spin 1s linear infinite' }}
                    />
                  </div>
                )}
                {s.aiStatus === 'success' && (
                  <div style={{
                    position: 'absolute', bottom: '2px', right: '2px',
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: C.success, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Check size={12} color="#fff" strokeWidth={3} />
                  </div>
                )}
                {s.aiStatus === 'error' && (
                  <div style={{
                    position: 'absolute', bottom: '2px', right: '2px',
                    width: '18px', height: '18px', borderRadius: '50%',
                    background: C.danger, color: '#fff',
                    fontSize: '11px', fontWeight: '700', lineHeight: '18px',
                    textAlign: 'center', fontFamily: fs,
                  }}>!</div>
                )}
                {/* Only allow removing additional shots (index > 0). The first
                    shot is the primary; removing it would orphan the working
                    item built from its AI response. */}
                {shots.length > 1 && shots[0].localId !== s.localId && (
                  <button
                    onClick={() => removeShot(s.localId)}
                    aria-label="Remove photo"
                    style={{
                      position: 'absolute', top: '-6px', right: '-6px',
                      width: '20px', height: '20px', borderRadius: '50%',
                      background: '#fff', border: `1px solid ${C.border}`,
                      cursor: 'pointer', padding: 0, lineHeight: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={11} color={C.muted} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={handleContinueFromBetweenShots}
            style={{
              width: '100%',
              background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '14px', fontFamily: f, fontSize: '15px',
              fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
            }}
          >
            Continue
          </button>
        </div>

        {/* Discard confirm */}
        {confirmDiscard && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}>
            <div style={{
              background: C.card, borderRadius: '14px', padding: '22px',
              maxWidth: '360px', width: '100%',
            }}>
              <div style={{ fontFamily: f, fontSize: '17px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>
                Discard these photos?
              </div>
              <div style={{ fontFamily: fs, fontSize: '13px', color: C.brown, lineHeight: 1.55, marginBottom: '18px' }}>
                You'll lose the {shots.length} photo{shots.length === 1 ? '' : 's'} you've taken so far.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={() => setConfirmDiscard(false)}
                  style={{
                    background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                    color: '#fff', border: 'none', borderRadius: '8px',
                    padding: '12px', fontFamily: f, fontSize: '14px',
                    fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Keep editing
                </button>
                <button
                  onClick={() => { setConfirmDiscard(false); onClose() }}
                  style={{
                    background: 'transparent', color: C.muted,
                    border: `1px solid ${C.border}`, borderRadius: '8px',
                    padding: '12px', fontFamily: fs, fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  function renderReview() {
    if (!item) return null
    const photoUrl = shots[0]?.url || firstResponse?.photo_url || item.thumbnail_url
    const detailPhotos = item.photo_urls || []
    // shots[0] is shown as the primary photo above; render shots[1..] in the
    // detail strip alongside any review-screen extras for visibility.
    const extraShotUrls = shots
      .slice(1)
      .filter(s => !s.isBlob && typeof s.url === 'string' && s.url.length > 0)
      .map(s => s.url)
    const atPhotoCap = (shots.length + detailPhotos.length) >= MAX_DETAIL_PHOTOS_TOTAL

    return (
      <>
        {renderHeader('Item Photo')}
        <div style={scrollArea}>
          {/* Main photo */}
          {photoUrl && (
            <div style={{ background: '#000', display: 'flex', justifyContent: 'center' }}>
              <img
                src={photoUrl}
                alt=""
                style={{ display: 'block', maxWidth: '100%', maxHeight: '35vh', width: 'auto', height: 'auto' }}
              />
            </div>
          )}

          {/* Change Room */}
          <div style={{ padding: '14px 14px 0' }}>
            <label style={lbl}>Room</label>
            <select
              value={chosenRoomId || ''}
              onChange={e => setChosenRoomId(e.target.value || null)}
              style={{ ...inp, background: '#fff' }}
            >
              <option value="">— No room (sort later) —</option>
              {roomsList.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>

          {/* Detail Photos */}
          <div style={{ padding: '20px 14px 0' }}>
            <div style={{
              fontSize: '10px', color: C.muted, letterSpacing: '2px',
              textTransform: 'uppercase', fontFamily: fs, fontWeight: '700',
              marginBottom: '10px',
            }}>
              Detail Photos
            </div>
            {(extraShotUrls.length > 0 || detailPhotos.length > 0) && (
              <div style={{
                display: 'flex', gap: '8px', overflowX: 'auto',
                paddingBottom: '8px', WebkitOverflowScrolling: 'touch',
              }}>
                {/* Capture-session extra shots (read-only on review — already
                    contributed AI-extracted details to the working item). */}
                {extraShotUrls.map((url, idx) => (
                  <div key={`shot-${idx}-${url}`} style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={url}
                      alt=""
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', background: C.bg }}
                    />
                  </div>
                ))}
                {detailPhotos.map((url, idx) => (
                  <div key={url + idx} style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={url}
                      alt=""
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', background: C.bg }}
                    />
                    <button
                      onClick={() => removeDetailPhoto(idx)}
                      aria-label="Remove photo"
                      style={{
                        position: 'absolute', top: '-6px', right: '-6px',
                        width: '22px', height: '22px', borderRadius: '50%',
                        background: '#fff', border: `1px solid ${C.border}`,
                        cursor: 'pointer', padding: 0, lineHeight: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <X size={12} color={C.muted} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={detailCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleDetailPhotoSelect}
            />
            <input
              ref={detailLibraryRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleDetailPhotoSelect}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: detailPhotos.length > 0 ? '8px' : 0 }}>
              <button
                onClick={() => detailCameraRef.current?.click()}
                disabled={atPhotoCap || photoUploading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: atPhotoCap || photoUploading ? C.bg : `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                  color: atPhotoCap || photoUploading ? C.muted : '#fff',
                  border: 'none', borderRadius: '10px',
                  padding: '12px 14px', fontFamily: f, fontSize: '14px',
                  fontWeight: '700',
                  cursor: atPhotoCap || photoUploading ? 'not-allowed' : 'pointer',
                  opacity: atPhotoCap || photoUploading ? 0.7 : 1,
                  justifyContent: 'flex-start',
                }}
              >
                <Camera size={16} strokeWidth={2} color={atPhotoCap || photoUploading ? C.muted : '#fff'} />
                {photoUploading ? 'Uploading…' : 'Take a Photo'}
              </button>
              <button
                onClick={() => detailLibraryRef.current?.click()}
                disabled={atPhotoCap || photoUploading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  background: '#fff', color: C.brown,
                  border: `1px solid ${C.border}`, borderRadius: '10px',
                  padding: '12px 14px', fontFamily: fs, fontSize: '14px',
                  fontWeight: '600',
                  cursor: atPhotoCap || photoUploading ? 'not-allowed' : 'pointer',
                  opacity: atPhotoCap || photoUploading ? 0.5 : 1,
                  justifyContent: 'flex-start',
                }}
              >
                <ImageIcon size={16} strokeWidth={2} color={C.brown} />
                Add from Camera Roll
              </button>
              {atPhotoCap && (
                <div style={{ fontFamily: fs, fontSize: '12px', color: C.muted }}>
                  Limit reached — {MAX_DETAIL_PHOTOS_TOTAL} photos per item.
                </div>
              )}
              {photoError && (
                <div style={{ fontFamily: fs, fontSize: '12px', color: C.danger }}>
                  {photoError}
                </div>
              )}
            </div>
          </div>

          {/* Item Details */}
          <div style={{ padding: '24px 14px 0' }}>
            <div style={{
              fontSize: '10px', color: C.muted, letterSpacing: '2px',
              textTransform: 'uppercase', fontFamily: fs, fontWeight: '700',
              marginBottom: '10px',
            }}>
              Item Details
            </div>

            <div>
              <label style={lbl}>Name</label>
              <input value={item.name || ''} onChange={e => setField('name', e.target.value)} style={inp} />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}><ConfDot conf={item.brand_confidence} /> Brand</label>
              <input value={item.brand || ''} onChange={e => setField('brand', e.target.value)} style={inp} placeholder="—" />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}><ConfDot conf={item.model_confidence} /> Model</label>
              <input value={item.model_number || ''} onChange={e => setField('model_number', e.target.value)} style={inp} placeholder="—" />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Serial</label>
              <input value={item.serial_number || ''} onChange={e => setField('serial_number', e.target.value)} style={inp} placeholder="—" />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Manufactured</label>
              <input value={item.manufactured || ''} onChange={e => setField('manufactured', e.target.value)} style={inp} placeholder="—" />
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Category</label>
              <select
                value={item.category || 'Other'}
                onChange={e => setField('category', e.target.value)}
                style={{ ...inp, background: '#fff' }}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Color</label>
                <input value={item.color || ''} onChange={e => setField('color', e.target.value)} style={inp} placeholder="—" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>Material</label>
                <input value={item.material || ''} onChange={e => setField('material', e.target.value)} style={inp} placeholder="—" />
              </div>
            </div>

            <div style={{ marginTop: '12px' }}>
              <label style={lbl}>Notes</label>
              <textarea
                value={item.notes || ''}
                onChange={e => setField('notes', e.target.value)}
                rows={3}
                style={{ ...inp, fontFamily: fs, resize: 'vertical', minHeight: '78px' }}
                placeholder="—"
              />
            </div>
          </div>

          <div style={{ height: '24px' }} />
        </div>

        {/* Sticky bottom save */}
        <div style={{
          flexShrink: 0,
          padding: '12px 14px calc(18px + env(safe-area-inset-bottom))',
          borderTop: `1px solid ${C.border}`,
          background: C.card,
        }}>
          <button
            onClick={handleSave}
            style={{
              width: '100%',
              background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
              color: '#fff', border: 'none', borderRadius: '10px',
              padding: '14px', fontFamily: f, fontSize: '15px',
              fontWeight: '700', cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(184,114,31,0.25)',
            }}
          >
            Save to inventory
          </button>
        </div>

        {/* Discard confirm */}
        {confirmDiscard && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.5)',
            zIndex: 1800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '24px',
          }}>
            <div style={{
              background: C.card, borderRadius: '14px', padding: '22px',
              maxWidth: '360px', width: '100%',
            }}>
              <div style={{ fontFamily: f, fontSize: '17px', fontWeight: '700', color: C.brown, marginBottom: '8px' }}>
                Discard this scanned item?
              </div>
              <div style={{ fontFamily: fs, fontSize: '13px', color: C.brown, lineHeight: 1.55, marginBottom: '18px' }}>
                You'll lose what you've captured so far. The photo upload won't be saved.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={() => setConfirmDiscard(false)}
                  style={{
                    background: `linear-gradient(180deg, ${C.amberLight} 0%, ${C.amber} 100%)`,
                    color: '#fff', border: 'none', borderRadius: '8px',
                    padding: '12px', fontFamily: f, fontSize: '14px',
                    fontWeight: '700', cursor: 'pointer',
                  }}
                >
                  Keep editing
                </button>
                <button
                  onClick={() => { setConfirmDiscard(false); onClose() }}
                  style={{
                    background: 'transparent', color: C.muted,
                    border: `1px solid ${C.border}`, borderRadius: '8px',
                    padding: '12px', fontFamily: fs, fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // ----- Render -------------------------------------------------------------

  return (
    <div style={overlay}>
      <style>{SPIN_STYLE}</style>
      {state === 'capture' && renderCapture()}
      {state === 'uploading' && renderUploading()}
      {state === 'between_shots' && renderBetweenShots()}
      {state === 'uploading_more' && renderUploading()}
      {state === 'review' && renderReview()}
      {state === 'saving' && renderSaving()}
      {state === 'error' && renderError()}
    </div>
  )
}

